import React, { useEffect } from 'react';
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { Auth } from '@whatsper/texterchat-common';
import ConnectionSpeed from '../components/ConnectionSpeed';
import ScenariosManager from '../components/ScenariosManager';
import TOTPSetup from '../components/TOTPSetup';
import ProtectedRoute from '../components/ProtectedRoute';
import { useStore } from '../store/rootStore';
import { DrawerLevel, ManagerFeature, ManagerOperation } from '../store/UIStore';
import SECTION from '../store/NavigationStore/Section';
import { MANAGER_FEATURE_TO_PATH, getViewPermissions } from '../components/SettingsDrawer/constants';
/**
 * Component that syncs URL params with UIStore for drawer-based managers
 * Permission checking is handled by ProtectedRoute wrapper
 */
const DrawerManagerRoute = observer(({ feature }) => {
    const { ui, navigation } = useStore();
    const params = useParams();
    const entityId = params.entityId;
    useEffect(() => {
        // Open settings drawer (first level)
        navigation.toggleSection(SECTION.settings, true);
        // Open second level with the feature
        if (!ui.isManagerDrawerOpen(DrawerLevel.SECOND) || ui.getManagerFeature() !== feature) {
            ui.openSecondLevelManager(feature);
        }
        // If there's an entity ID, open third level
        if (entityId) {
            if (!ui.isManagerDrawerOpen(DrawerLevel.THIRD) || ui.getManagerItemId() !== entityId) {
                ui.openThirdLevelManager(entityId, ManagerOperation.EDIT);
            }
        }
    }, [feature, entityId, navigation, ui]);
    return null;
});
/**
 * Routes for /manage/* paths
 */
const ManagersRoute = observer(() => {
    const history = useHistory();
    const location = useLocation();
    const { ui, loginStore } = useStore();
    // Update URL when drawer state changes
    useEffect(() => {
        const dispose = reaction(() => ({
            feature: ui.getManagerFeature(),
            itemId: ui.getManagerItemId(),
            isSecondOpen: ui.isManagerDrawerOpen(DrawerLevel.SECOND),
            isThirdOpen: ui.isManagerDrawerOpen(DrawerLevel.THIRD),
        }), ({ feature, itemId, isSecondOpen, isThirdOpen }) => {
            if (!isSecondOpen) {
                if (location.pathname.startsWith('/manage/')) {
                    history.push('/');
                }
                return;
            }
            if (!feature || !MANAGER_FEATURE_TO_PATH[feature]) {
                return;
            }
            const featurePath = MANAGER_FEATURE_TO_PATH[feature];
            if (isThirdOpen && itemId) {
                const newPath = `/manage/${featurePath}/${itemId}`;
                if (location.pathname !== newPath) {
                    history.replace(newPath);
                }
            }
            else if (isSecondOpen) {
                const newPath = `/manage/${featurePath}`;
                if (location.pathname !== newPath) {
                    history.replace(newPath);
                }
            }
        });
        return () => dispose();
    }, [history, location.pathname, ui]);
    return (React.createElement(Switch, null,
        React.createElement(Route, { path: "/manage/scenarios/:scenarioId?", component: ScenariosManager }),
        React.createElement(Route, { exact: true, path: "/manage/developers/connection-speed", component: ConnectionSpeed }),
        React.createElement(ProtectedRoute, { exact: true, path: "/manage/security/totp-setup", condition: () => { var _a; return !!((_a = loginStore.availableAuthMethods) === null || _a === void 0 ? void 0 : _a.includes(Auth.Methods.Method.totp)); }, component: TOTPSetup }),
        React.createElement(ProtectedRoute, { path: "/manage/labels/:entityId?", requiredPermissions: getViewPermissions(ManagerFeature.LABELS), render: () => React.createElement(DrawerManagerRoute, { feature: ManagerFeature.LABELS }) }),
        React.createElement(ProtectedRoute, { path: "/manage/agents/:entityId?", requiredPermissions: getViewPermissions(ManagerFeature.AGENTS), render: () => React.createElement(DrawerManagerRoute, { feature: ManagerFeature.AGENTS }) }),
        React.createElement(ProtectedRoute, { path: "/manage/departments/:entityId?", requiredPermissions: getViewPermissions(ManagerFeature.DEPARTMENTS), render: () => React.createElement(DrawerManagerRoute, { feature: ManagerFeature.DEPARTMENTS }) }),
        React.createElement(ProtectedRoute, { path: "/manage/channels/:entityId?", requiredPermissions: getViewPermissions(ManagerFeature.CHANNELS), render: () => React.createElement(DrawerManagerRoute, { feature: ManagerFeature.CHANNELS }) }),
        React.createElement(ProtectedRoute, { path: "/manage/developers/api-tokens/:entityId?", requiredPermissions: getViewPermissions(ManagerFeature.API_TOKENS), render: () => React.createElement(DrawerManagerRoute, { feature: ManagerFeature.API_TOKENS }) }),
        React.createElement(ProtectedRoute, { path: "/manage/developers/oauth/:entityId?", requiredPermissions: getViewPermissions(ManagerFeature.OAUTH), render: () => React.createElement(DrawerManagerRoute, { feature: ManagerFeature.OAUTH }) })));
});
export default ManagersRoute;
